import { useId, useState, type RefObject } from "react";
import { CoreStandardModal, type HeadingLevel } from "@boi/components-core";
import { useDesignTokens } from "@boi/react-design-tokens";
import { CloseIcon } from "@boi/react-icons";

import * as S from "./StandardModal.styled";
import { useHeadingVisible } from "./StandardModal.utils";
import { Modal, type ModalProps } from "../Modal";
import { IconButton, type IconButtonProps } from "../IconButton";
import { Typography } from "../Typography";
import { ScrollShadowContainer } from "../ScrollShadowContainer";

export type StandardModalProps = ModalProps & {
  onRequestClose: Exclude<ModalProps["onRequestClose"], undefined>;
  heading: string;
  headingLevel?: HeadingLevel;
  closeButtonProps?: Omit<IconButtonProps, "onClick" | "children">;
};

function StandardModal(props: StandardModalProps) {
  const {
    heading,
    headingLevel = CoreStandardModal.DEFAULT_HEADING_LEVEL,
    closeButtonProps,
    children,
    testID = "standard-modal",
    ...modalProps
  } = props;

  const { isOpen, onRequestClose } = modalProps;

  const { tokens } = useDesignTokens();
  const { standardModal } = tokens;

  const headingID = useId();

  return (
    <Modal {...modalProps} aria-labelledby={headingID} testID={testID}>
      {isOpen && (
        <StandardModalState>
          {({
            borderVisible,
            setBorderVisible,
            headingRef,
            headingVisible,
          }) => (
            <S.Container>
              <S.Header $borderVisible={borderVisible}>
                <S.MiniHeadingWrapper
                  $headingVisible={headingVisible}
                  {...{ inert: "" }}
                >
                  <Typography
                    noWrap
                    component="p"
                    variant={standardModal.header.heading.typography.variant}
                    fontFamily={
                      standardModal.header.heading.typography.fontFamily
                    }
                  >
                    {heading}
                  </Typography>
                </S.MiniHeadingWrapper>

                <S.CloseButtonWrapper>
                  <IconButton
                    aria-label="Close modal"
                    testID={`${testID}-close-button`}
                    {...closeButtonProps}
                    onClick={onRequestClose}
                    variant={standardModal.header.closeButton.variant}
                  >
                    <CloseIcon />
                  </IconButton>
                </S.CloseButtonWrapper>
              </S.Header>

              <ScrollShadowContainer
                onChange={({ topShadowVisible }) => {
                  setBorderVisible(topShadowVisible);
                }}
              >
                <S.Content>
                  <Typography
                    ref={headingRef}
                    id={headingID}
                    component={`h${headingLevel}`}
                    variant={standardModal.content.heading.typography.variant}
                    fontFamily={
                      standardModal.content.heading.typography.fontFamily
                    }
                  >
                    {heading}
                  </Typography>

                  <div>{children}</div>
                </S.Content>
              </ScrollShadowContainer>

              <S.Footer aria-hidden="true" />
            </S.Container>
          )}
        </StandardModalState>
      )}
    </Modal>
  );
}

type StandardModalStateProps = {
  children: ({
    borderVisible,
    setBorderVisible,
    headingRef,
    headingVisible,
  }: {
    borderVisible: boolean;
    setBorderVisible: (value: boolean) => void;
    headingRef: RefObject<HTMLHeadingElement>;
    headingVisible: boolean;
  }) => JSX.Element;
};

function StandardModalState(props: StandardModalStateProps) {
  const { children } = props;

  const [borderVisible, setBorderVisible] = useState(false);
  const { headingRef, headingVisible } = useHeadingVisible();

  return children({
    borderVisible,
    setBorderVisible,
    headingRef,
    headingVisible,
  });
}

export default StandardModal;
